import React from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import * as AuthApi from "features/auth/api/AuthApi"

import { useForm, useAlert, useOperator } from "hooks"
import { SignupActionType, useSignupProcess } from "../hooks/useSignupProcess"

import PublicLayout from "components/Layout/PublicLayout"
import { publicRoutes } from "router/routes"

import { LeftWave, RightWave, Button, Spinner } from "components/Elements"
import { formStatusEnum, FormElement } from "components/Form"

import { ErrorResponse, UserOnSignup } from "models"
import { ACCEPTED_NOTIFICATION, CGU_VERSION, PRIVATE_GENDER_VALUE } from "utils"
import { Seo } from "components/Seo"
import { BICYCODE_ID } from "operators/bicycode/config"

type ValidateFormProps = Record<string, never>

export const ValidateSignupPage: React.FC<ValidateFormProps> = () => {
	const { signupState } = useSignupProcess()

	const defaultFormFields: UserOnSignup = signupState?.data

	const {
		formFields,
		setFormFields,
		formStatus,
		setFormStatus,
		handleChange,
		errors,
		showLocalErrors,
		setShowLocalErrors,
		handleValidation,
	} = useForm({
		defaultFormFields: defaultFormFields,
	})

	const { t } = useTranslation()
	const navigate = useNavigate()
	const { alert } = useAlert()
	const { currentOperator } = useOperator()
	const { signupDispatch } = useSignupProcess()

	const isLoading = formStatus === formStatusEnum.loading
	const isReady = formStatus === formStatusEnum.ready

	const resendCode = async () => {
		try {
			setFormStatus(formStatusEnum.loading)

			const { getError } = await AuthApi.signUpCheckMail(
				formFields.description.mail,
				formFields.operator
			)
			if (getError()) throw getError()
			alert("success", t("code_resend", { ns: "form" }))
		} catch (error) {
			console.error(error)
			const errorMsg = (error as ErrorResponse)?.message?.toString()
			alert("error", errorMsg)
		} finally {
			setFormStatus(formStatusEnum.ready)
			setFormFields((s: any) => ({
				...s,
				code: "",
			}))
		}
	}

	const handleSubmit = async (event: React.MouseEvent<HTMLButtonElement>) => {
		event.preventDefault()
		// Validate inputs
		const formReady = !(errors.length > 0)

		if (!formReady) {
			setShowLocalErrors(true)
		} else {
			try {
				setFormStatus(formStatusEnum.loading)

				const userDescription = formFields.description

				const newUserDescription = {
					gender: parseInt(userDescription.gender),
					phone: userDescription.phone.trim(),
					mail: userDescription.mail.trim(),
					...(() => {
						if (userDescription.gender === PRIVATE_GENDER_VALUE.toString()) {
							return {
								first_name: userDescription.first_name.trim(),
								last_name: userDescription.last_name.trim(),
							}
						} else {
							return {
								social_reason: userDescription.social_reason.trim(),
							}
						}
					})(),
				}

				const newUser = {
					description: newUserDescription,
					password: formFields.password,
					operator: formFields.operator,
					code: formFields.code,
					cgu_version: CGU_VERSION,
					...(() => {
						if (formFields.operator_notifications_accepted) {
							return {
								operator_notifications_accepted: ACCEPTED_NOTIFICATION,
							}
						}
					})(),
					...(() => {
						if (formFields.ocode_notifications_accepted) {
							return {
								ocode_notifications_accepted: ACCEPTED_NOTIFICATION,
							}
						}
					})(),
				}

				const { getError } = await AuthApi.signUp(newUser)

				if (getError()) throw getError()
				alert("success", t("success_create_account", { ns: "form" }))
				navigate(publicRoutes.signin.path)
			} catch (error) {
				console.error(error)
				const errorMsg = (error as ErrorResponse)?.message?.toString()
				alert("error", errorMsg)
			} finally {
				setFormStatus(formStatusEnum.ready)
			}
		}
	}

	const formElementCommonProps = {
		showErrors: showLocalErrors,
		onValidate: handleValidation,
		onChange: handleChange,
	}

	const mainUrl =
		process.env.REACT_APP_MODE === "production"
			? `${window.location.origin}`
			: `${process.env.PUBLIC_URL}`

	return (
		<PublicLayout classNameMain="justify-center">
			<div className="max-w-screen-3xl mx-auto">
				<Seo
					title={t("signup_title", {
						ns: "seo",
						operator: currentOperator.name,
					})}
					description={t("signup_description", {
						ns: "seo",
						operator: currentOperator.name,
					})}
				/>
				<LeftWave className="fixed bottom-0 left-0" />
				<RightWave className="fixed bottom-0 right-0" />

				{currentOperator.id === BICYCODE_ID && (
					<img
						src={`${mainUrl}/images/Chevalier_2.png`}
						alt=""
						width="460"
						height="460"
						className="hidden lg:block fixed bottom-2 right-0"
					/>
				)}

				{signupState?.data && (
					<div className="flex flex-col justify-center relative lg:pt-6 pt-2">
						<h1 className="c-h1 text-primary mb-8 text-center">
							{t("signup.received_code_title", {
								ns: "auth",
							})}
						</h1>
						<p className="max-w-2xl text-lg mx-auto mb-10 text-center">
							{t("signup.received_code_description", {
								ns: "auth",
							})}
							<span className="font-bold">{formFields.description.mail}</span>,
							{t("signup.received_code_description_2", {
								ns: "auth",
							})}
						</p>

						<form className="mx-auto lg:w-10/12 w-full md:px-4" autoComplete="off">
							<div className="grid justify-center">
								<FormElement
									{...formElementCommonProps}
									value={formFields.code}
									name="code"
									type="code"
									id="code"
									classNameLabel="text-primary font-bold mb-4"
									className="mb-2 mx-auto"
									required={true}
								/>

								<button
									type="button"
									onClick={resendCode}
									className="underline text-xxs mb-6 justify-self-end"
								>
									{t("resend_code", { ns: "user" })}
								</button>
							</div>

							{isReady && (
								<div className="flex items-center mt-6 md:mt-12 justify-center">
									<Button
										variant="secondary"
										type="submit"
										onClick={handleSubmit}
										className="mx-1 px-10 py-1 lg:mx-3"
									>
										{t("global.validate")}
									</Button>

									<Button
										variant="secondaryBordered"
										type="button"
										onClick={() => {
											signupDispatch({
												type: SignupActionType.INIT,
												payload: formFields,
											})
										}}
										className="mx-1 px-10 py-1 lg:mx-3"
									>
										{t("global.cancel")}
									</Button>
								</div>
							)}

							{isLoading && (
								<div className="flex justify-center my-8">
									<Spinner color={"var(--primaryColor)"} size={"100px"} />
								</div>
							)}
						</form>
					</div>
				)}
			</div>
		</PublicLayout>
	)
}
